// Bridge entre V1 parent (top-level) et l'iframe V2.
// Gère la permission, l'enregistrement SW, la subscription, la délégation postMessage.
// Lit le JWT depuis localStorage (clé existante 'iot_rocket_access_JWT').

const V2_ORIGIN  = 'https://1e632f9ca899.ngrok.app';       // ex: https://<ngrok>.ngrok.app
const V2_BASE    = 'https://1e632f9ca899.ngrok.app';      // même URL pour les fetchs
const VAPID_PUB  = 'BCEsDvhpUxFWudbHXyLWqnN2a-_Iixke7W0KJrFqajQAVnXU7TutWi2eUw4vOLDkIONgIxa2vv4l_YXoheGSnlc'; // build-time, pas secret
const SW_PATH    = '/sw-webpush-v3.js';
const SW_SCOPE   = '/notifications-v3/';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// ⚠️ navigator.serviceWorker.getRegistration(url) retourne le SW dont le scope
// est le PRÉFIXE LE PLUS LONG matchant cette URL — pas le SW au scope exact.
// Si on a un autre SW au scope `/` (ex: Workbox/Firebase Messaging existant), il
// matche aussi `/notifications-v3/`. Sans cette garde, on récupèrerait sa
// subscription par erreur (et on la POSTerait à V2, ce qui crée une row
// orpheline qui répond 403 sur push à cause du mismatch de clé VAPID).
async function getV3Registration() {
  const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (!reg) return null;
  const expectedScope = new URL(SW_SCOPE, location.origin).href;
  if (reg.scope !== expectedScope) return null;
  return reg;
}

// Lit les UA Client Hints high-entropy quand disponibles (Chromium uniquement).
// Sur Android, le User-Agent est réduit ("Android 10; K") — c'est ici qu'on
// récupère le vrai modèle (ex: "Pixel 8") et la vraie version OS (ex: "14.0.0").
async function getDeviceHints() {
  if (!('userAgentData' in navigator)) return null;
  try {
    const h = await navigator.userAgentData.getHighEntropyValues(['model', 'platformVersion']);
    return { model: h.model || null, platformVersion: h.platformVersion || null };
  } catch (_) {
    return null;
  }
}

// Construit le body POST commun pour /api/subscriptions et /api/subscriptions/heartbeat.
async function buildSubscriptionBody(sub) {
  const hints = await getDeviceHints();
  return {
    subscription: sub.toJSON(),
    userAgent: navigator.userAgent,
    deviceModel: hints?.model ?? null,
    osVersion: hints?.platformVersion ?? null,
  };
}

// POST /api/subscriptions : utilisé par subscribeOnce (geste utilisateur "Activer").
// Crée la row si absente, re-active si désactivée.
async function postSubscriptionToV2(sub) {
  const jwt = localStorage.getItem('iot_rocket_access_JWT');
  if (!jwt) throw new Error('no_jwt');
  const resp = await fetch(`${V2_BASE}/api/subscriptions`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(await buildSubscriptionBody(sub)),
  });
  if (!resp.ok) throw new Error('v2_post_failed');
}

// POST /api/subscriptions/heartbeat : utilisé par reconcile (silent re-sync au boot).
// NE crée PAS de row : si V2 ne connait plus l'endpoint (suppression depuis un autre
// device), V2 répond 410 → on unsubscribe le SW local pour ne pas re-créer un zombie.
async function heartbeatToV2(sub) {
  const jwt = localStorage.getItem('iot_rocket_access_JWT');
  if (!jwt) return;
  const resp = await fetch(`${V2_BASE}/api/subscriptions/heartbeat`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(await buildSubscriptionBody(sub)),
  });
  if (resp.status === 410) {
    // V2 a oublié cet endpoint → nettoyer côté browser
    try { await sub.unsubscribe(); } catch (_) {}
  }
}

async function subscribeOnce() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('unsupported');
  }
  if (!VAPID_PUB) throw new Error('missing_vapid_public_key');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('permission_denied');
  const reg = await navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE });
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUB),
    });
  }
  await postSubscriptionToV2(sub);
}

async function unsubscribeAll() {
  const reg = await getV3Registration();   // ← utilise la garde de scope exact
  const sub = await reg?.pushManager.getSubscription();
  if (sub) await sub.unsubscribe();
}

// Re-sync proactif (sans localStorage). Heartbeat-only : ne re-crée jamais de row.
// Si V2 ne connait plus l'endpoint (suppression depuis un autre device, par exemple),
// V2 répond 410 et heartbeatToV2 fait un unsubscribe local pour rester cohérent.
async function reconcile() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await getV3Registration();   // ← scope exact, ne touche pas un autre SW
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try { await heartbeatToV2(sub); } catch (_) {}
}

function getPermissionState() {
  return ('Notification' in window) ? Notification.permission : 'unsupported';
}

// Récupère l'endpoint de la subscription courante du browser (null si pas subscribed).
// L'iframe en a besoin pour identifier laquelle de ses rows correspond à CE browser-ci
// (multi-device : on peut avoir Chrome + Firefox + mobile, chacun avec sa propre row).
async function getCurrentEndpoint() {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await getV3Registration();
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription();
  return sub?.endpoint ?? null;
}

function replyToIframe(type, extra = {}) {
  const iframe = document.getElementById('notifications-v3-iframe');
  if (!iframe || !iframe.contentWindow) return;
  iframe.contentWindow.postMessage({ type, ...extra }, V2_ORIGIN);
}

async function replyCurrentEndpoint() {
  replyToIframe('currentEndpoint', { endpoint: await getCurrentEndpoint() });
}

// Force le browser à re-fetch le fichier SW pour détecter les éventuelles
// mises à jour. À appeler au boot et à chaque ouverture de la popup V3.
// Si une nouvelle version est trouvée, le SW est installé + activé immédiatement
// grâce à skipWaiting() + clients.claim() qu'on a déjà dans sw-webpush-v3.js.
async function checkForSWUpdate() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await getV3Registration();
  if (!reg) return;

  // Listener avant l'update() : on capte le nouveau worker dès qu'il commence à s'installer
  reg.addEventListener('updatefound', () => {
    const newWorker = reg.installing;
    if (!newWorker) return;
    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'activated') {
        replyToIframe('swUpdated');   // ← l'iframe peut afficher un message
      }
    });
  });

  try { await reg.update(); } catch (_) {}
}

export function installNotificationsV3Bridge() {
  // Reconcile au boot V1
  reconcile();

  // Check for SW update
  checkForSWUpdate();

  // Listener Permissions API: détecte une révocation depuis les settings du navigateur
  if ('permissions' in navigator) {
    navigator.permissions.query({ name: 'notifications' }).then((status) => {
      status.addEventListener('change', async () => {
        replyToIframe('permission', { state: status.state });
        if (status.state === 'denied') {
          try { await unsubscribeAll(); } catch (_) {}
        }
        replyCurrentEndpoint();
      });
    }).catch(() => {});
  }

  window.addEventListener('message', async (event) => {
    if (event.origin !== V2_ORIGIN) return;
    const msg = event.data;
    if (!msg || typeof msg !== 'object') return;

    if (msg.type === 'iframe-ready') {
      const jwt = localStorage.getItem('iot_rocket_access_JWT');
      if (jwt) replyToIframe('token', { data: jwt });
      replyToIframe('permission', { state: getPermissionState() });
      await replyCurrentEndpoint();
      reconcile();
      checkForSWUpdate();
      return;
    }

    if (msg.type === 'subscribeRequest') {
      try {
        await subscribeOnce();
        replyToIframe('subscribed');
      } catch (e) { replyToIframe('subscribeFailed', { reason: e.message }); }
      await replyCurrentEndpoint();
      return;
    }

    if (msg.type === 'unsubscribeRequest') {
      try {
        await unsubscribeAll();
        replyToIframe('unsubscribed');
      } catch (e) { replyToIframe('unsubscribeFailed', { reason: e.message }); }
      await replyCurrentEndpoint();
    }
  });
}